import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { Badge, Fab, IconButton, Tooltip } from '@mui/material';
import { Add as AddIcon, Info as InfoIcon, HelpCenter as QuestionInfo } from '@mui/icons-material';

// Services
import { IActivity, ICustomRepository, ISelfPlacementResult } from 'services/interfaces';
import { EMastery, ERepositoryItemType, EStepType } from 'services/enums';

// Views
import Tasks from './tasks';
import ObservableElements from './observable-elements';
import { getSelfResultIcon, RatingButton, RatingSelfMasteryButton, ResponsiveTable, RatingGlobalButton, SelfResultInfos } from '../utils';
import { RepositoryItemsFormContext, IRepositoryItemsFormContext } from '../../index';

interface IState {
    selectedActivity: IActivity | undefined;
    openSelfResultInfos: boolean;
    openTasks: boolean;
    openObservableElements: boolean;
}

// ---------------------------------------------------------------------------------------------------- \\
// ---------------------------------------- RATING ACTIVITIES ----------------------------------------- \\
// ---------------------------------------------------------------------------------------------------- \\

export default function RatingActivities({
    customRepository,
    selfPlacementResults,
} : {
    customRepository: ICustomRepository | undefined,
    selfPlacementResults: ISelfPlacementResult[],
}) {

    const { t } = useTranslation();

    const {
        setRepositoryItems,
        stepType,
        activities: repositoryItems,
        enabledObservableElements,
    } = useContext(RepositoryItemsFormContext) as IRepositoryItemsFormContext;

    const [state, setState] = useState<IState>({
        selectedActivity: undefined,
        openSelfResultInfos: false,
        openTasks: false,
        openObservableElements: false,
    });

    return (
        <>
            <table>
                <thead>
                    <tr>
                        <td />
                        {(
                            stepType !== EStepType.SELF_PLACEMENT
                            && repositoryItems.length > 1
                        ) ? (
                            <td className="table-td">
                                <RatingGlobalButton
                                    repositoryItemType={ERepositoryItemType.ACTIVITY}
                                    repositoryItems={repositoryItems}
                                    setRepositoryItems={setRepositoryItems}
                                />
                            </td>
                        ) : (
                            <td />
                        )}
                        {selfPlacementResults.length > 0 && (
                            <td
                                className="table-td"
                                style={{ textAlign: 'center' }}
                            >
                                <Tooltip title={t('self_placement_results')}>
                                    <IconButton onClick={() => setState(prevState => ({ ...prevState, openSelfResultInfos: true }))}>
                                        <QuestionInfo fontSize="large" />
                                    </IconButton>
                                </Tooltip>
                                {state.openSelfResultInfos && (
                                    <SelfResultInfos
                                        title={t('self_placement_results')}
                                        onClose={() => setState(prevState => ({ ...prevState, openSelfResultInfos: false }))}
                                    />
                                )}
                            </td>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {repositoryItems.map((repositoryItem, index) => {

                        let activity = customRepository?.activityRepository?.activities?.find(a => a.id === repositoryItem.id);

                        return (
                            <ResponsiveTable
                                key={index}
                                index={index}
                                children1={(
                                    <div className="inline-flex">
                                        <span>
                                            {activity?.position}. {activity?.name}
                                        </span>
                                        {(
                                            activity?.tasks
                                            && activity.tasks.some(t => t.successIndicators.length > 0)
                                        ) && (
                                            <Tooltip title={t('tasks_and_success_indicators')}>
                                                <IconButton
                                                    onClick={() => setState(prevState => ({ ...prevState, openTasks: true, selectedActivity: activity }))}
                                                    style={{ padding: 6 }}
                                                >
                                                    <InfoIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                                children2={(
                                    (stepType === EStepType.SELF_PLACEMENT) ? (
                                        <RatingSelfMasteryButton
                                            itemId={repositoryItem.id}
                                            selfMastery={repositoryItem.selfMastery}
                                            repositoryItemType={ERepositoryItemType.ACTIVITY}
                                            repositoryItems={repositoryItems}
                                            setRepositoryItems={setRepositoryItems}
                                        />
                                    ) : (
                                        <RatingButton
                                            itemId={repositoryItem.id}
                                            mastery={repositoryItem.mastery}
                                            repositoryItemType={ERepositoryItemType.ACTIVITY}
                                            repositoryItems={repositoryItems}
                                            setRepositoryItems={setRepositoryItems}
                                        />
                                    )
                                )}
                                children3={(
                                    selfPlacementResults.length > 0
                                    ? getSelfResultIcon(repositoryItem.id, ERepositoryItemType.ACTIVITY, selfPlacementResults)
                                    : undefined
                                )}
                                children4={(
                                    (
                                        enabledObservableElements
                                        && repositoryItem.mastery === EMastery.NOT_VALIDATED
                                        && activity?.tasks?.some(t => t.observableElements.length > 0)
                                    ) ? (
                                        <Tooltip title={t('specify_observable_elements_to_work_on')}>
                                            <Badge
                                                badgeContent={repositoryItem.observableElements?.length}
                                                invisible={repositoryItem.observableElements == null || repositoryItem.observableElements.length === 0}
                                                color="secondary"
                                                overlap="circular"
                                            >
                                                <Fab
                                                    onClick={() => setState(prevState => ({ ...prevState, openObservableElements: true, selectedActivity: activity }))}
                                                    style={{ boxShadow: 'none' }}
                                                >
                                                    <AddIcon />
                                                </Fab>
                                            </Badge>
                                        </Tooltip>
                                    )
                                    : undefined
                                )}
                            />
                        );
                    })}
                </tbody>
            </table>
            {/* -------------------- TASKS -------------------- */}
            {(state.openTasks && state.selectedActivity) && (
                <Tasks
                    activity={state.selectedActivity}
                    onClose={() => setState(prevState => ({ ...prevState, openTasks: false }))}
                />
            )}
            {/* -------------------- OBSERVABLE ELEMENTS -------------------- */}
            {(enabledObservableElements && state.openObservableElements && state.selectedActivity) && (
                <ObservableElements
                    activity={state.selectedActivity}
                    onClose={() => setState(prevState => ({ ...prevState, openObservableElements: false }))}
                    repositoryItems={repositoryItems}
                    setRepositoryItems={setRepositoryItems}
                />
            )}
        </>
    );
}
